import { trackEvent } from "@analytics";
import { Body, Button, Icon, Stack } from "@biom3/react";
import { useMemo, useState } from "react";
import { NotEnoughGemsMessage } from "./NotEnoughGemsMessage";

type RedeemKeyButtonProps = {
  onOpenRulesModal?: () => void;
  onCloseRulesModal?: () => void;
  onRedeemTicket: () => Promise<void>;
  hasFreeEntry?: boolean;
  hasTicketUpgrade?: boolean;
  hasEnoughGems?: boolean;
  entryFee: string | number;
  shouldDirectlyRedeem?: boolean;
};

export const RedeemKeyButton = ({
  onOpenRulesModal,
  onCloseRulesModal,
  onRedeemTicket,
  hasFreeEntry,
  hasTicketUpgrade,
  hasEnoughGems,
  entryFee,
  shouldDirectlyRedeem = false,
}: RedeemKeyButtonProps) => {
  const [isRedeeming, setIsRedeeming] = useState(false);

  const handleRedeem = async () => {
    try {
      trackEvent({
        component: "Button",
        action: "Pressed",
        extras: {
          control: "RedeemKey",
          section: "RewardsRedemption",
          userJourney: "Rewards",
        },
      });

      setIsRedeeming(true);
      await onRedeemTicket();
    } catch (error) {
      console.error("Failed to redeem ticket", error);
    } finally {
      setIsRedeeming(false);
    }
  };

  const isDisabled = !hasFreeEntry && !hasEnoughGems;

  const getButtonContent = useMemo(() => {
    if (hasTicketUpgrade) {
      return {
        message: `Get an upgraded Key for ${entryFee}`,
        icon: "ImxRewards" as const,
      };
    }

    if (Number(entryFee) > 0) {
      return {
        message: `Get a Key for ${entryFee}`,
        icon: "ImxRewards" as const,
      };
    }

    return {
      message: "Get a free Key",
      icon: "ImxRewards" as const,
    };
  }, [entryFee, hasTicketUpgrade]);

  const handleClick = async () => {
    if (shouldDirectlyRedeem) {
      await handleRedeem();
      onCloseRulesModal?.();
    } else if (onOpenRulesModal) {
      trackEvent({
        component: "Button",
        action: "Pressed",
        extras: {
          control: "OpenRules",
          section: "RewardsRedemption",
          userJourney: "Rewards",
        },
      });
      onOpenRulesModal();
    }
  };

  return (
    <Stack>
      <Button
        disabled={isDisabled}
        size="large"
        onClick={handleClick}
        sx={
          shouldDirectlyRedeem
            ? { width: "100%", mt: "base.spacing.x4" }
            : { width: { large: "312px", default: "auto" } }
        }
      >
        {isRedeeming ? <Button.Icon icon="Loading" /> : null}
        <Stack gap="0px">
          <Body
            sx={{ c: "inherit", display: "flex", alignItems: "center" }}
            weight="bold"
          >
            {getButtonContent.message}
            <Icon
              icon={getButtonContent.icon}
              sx={{
                fill: "inherit",
                w: "base.icon.size.200",
                ml: "base.spacing.x1",
              }}
              variant="bold"
            />
          </Body>
        </Stack>
      </Button>
      {!hasFreeEntry && !hasEnoughGems && <NotEnoughGemsMessage />}
    </Stack>
  );
};
